import type { Order } from '../../../types/apiResponse/order';
import type { ColumnField } from './customColumns';

const field = (
  column: string, label: string, numeric = false, mode: 'sum' | 'latest' | 'distinct' = 'distinct',
  alias?: keyof Order,
): ColumnField => ({ path: `history_returns.${column}`, label, numeric, history: { column, mode, alias } });

/** Explicit fields from histpedido; identifiers are text even when they contain only digits. */
export const historyColumnFields: ColumnField[] = [
  field('NumPedEmpresa', 'Número do pedido no ERP'),
  field('NumPedDisp', 'Referência ao pedido SmartSell'),
  field('CodigoEmpresa', 'Código da empresa'),
  field('CodigoCliente', 'Código do cliente'),
  field('CodigoVendedor', 'Código do vendedor'),
  field('StatusPedido', 'Status do pedido'),
  field('ValorPedido', 'Valor do pedido', true, 'sum', 'returned_value'),
  field('ValorFaturado', 'Valor faturado', true, 'sum', 'billed_value'),
  field('DataPedido', 'Data do pedido', false, 'latest'),
  field('DataPrevEntrega', 'Data prevista de entrega', false, 'latest'),
  field('DataFaturamento', 'Data de faturamento', false, 'latest'),
  field('OrigemPedido', 'Origem do pedido'),
  field('CodigoTipoPedido', 'Código do tipo de pedido'),
  field('CodigoCondPagto', 'Código da condição de pagamento'),
  field('CodigoUnidFat', 'Código da unidade de faturamento'),
  field('DescricaoFrete', 'Descrição do frete'),
  field('DescontoCondPagto', 'Desconto da condição de pagamento', true),
  field('ValorComissao', 'Valor da comissão', true),
  field('DescFinanceiro', 'Desconto financeiro', true),
  field('PesoResidual', 'Peso residual', true),
  field('Cubagem', 'Cubagem', true),
  field('EliminaSaldo', 'Elimina saldo'),
  field('Observacao', 'Observação'),
  field('Cor', 'Cor'),
  field('created_at', 'Data de criação', false, 'latest'),
  field('updated_at', 'Data de atualização', false, 'latest'),
];

/** Missing history remains missing; it never falls back to commercial values from pedidos. */
export function readHistoryColumnField(order: Order, definition: ColumnField, numeric: boolean): unknown {
  const history = definition.history!;
  if (history.alias && order[history.alias] !== undefined) return order[history.alias];
  const values = (order.history_returns || []).map((row) => row[history.column])
    .filter((value) => value !== null && value !== undefined && String(value).trim() !== '');
  if (!values.length) return null;
  if (definition.numeric) {
    const numbers = values.map((value) => {
      if ((typeof value !== 'number' && typeof value !== 'string') || !Number.isFinite(Number(value))) {
        throw new Error(`O histórico contém um valor inválido para ${definition.label}.`);
      }
      return Number(value);
    });
    if (history.mode === 'sum') return numbers.reduce((total, value) => total + value, 0);
    const distinct = [...new Set(numbers)];
    if (distinct.length === 1) return distinct[0];
    if (numeric) throw new Error(`Há valores diferentes nos históricos para ${definition.label}.`);
    return distinct.join(', ');
  }
  const distinct = [...new Set(values.map((value) => String(value).trim()))];
  if (history.mode === 'latest') {
    const dates = distinct.filter((value) => !value.startsWith('0000-'));
    return dates.sort().pop() || null;
  }
  return distinct.join(', ');
}
